// Filter der Opportunity-Seiten in der Adresse (teilbar, "Link kopieren" liefert genau diese Ansicht). Board und Liste
// teilen sich den Filter: der Umschalter hängt die Suchparameter an.
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { Zeitraum } from "./logik";

export interface FilterWerte {
  pipeline: string | null;
  /** "me" oder Nutzerkennung */
  nutzer: string | null;
  /** Smart View der Leads */
  view: string | null;
  feld: "close" | "created";
  zeitraum: Zeitraum;
  /** nur Liste: Status-Auswahl */
  status: string[];
  /** nur Liste: Textsuche */
  q: string;
  /** API-Sortierung, null = Standard der Ansicht */
  sort: string | null;
}

const SCHLUESSEL = ["pipeline", "nutzer", "view", "feld", "von", "bis", "status", "q", "sort"] as const;

export function filterAusParametern(p: URLSearchParams): FilterWerte {
  return {
    pipeline: p.get("pipeline"),
    nutzer: p.get("nutzer"),
    view: p.get("view"),
    feld: p.get("feld") === "created" ? "created" : "close",
    zeitraum: { von: p.get("von"), bis: p.get("bis") },
    status: (p.get("status") ?? "").split(",").filter(Boolean),
    q: p.get("q") ?? "",
    sort: p.get("sort"),
  };
}

export function useFilter() {
  const [params, setParams] = useSearchParams();
  const werte = useMemo(() => filterAusParametern(params), [params]);

  const setze = useCallback(
    (teil: Partial<FilterWerte>) => {
      setParams(
        (alt) => {
          const neu = new URLSearchParams(alt);
          const setzen = (k: string, v: string | null | undefined) => {
            if (v) neu.set(k, v);
            else neu.delete(k);
          };
          if ("pipeline" in teil) setzen("pipeline", teil.pipeline);
          if ("nutzer" in teil) setzen("nutzer", teil.nutzer);
          if ("view" in teil) setzen("view", teil.view);
          if ("feld" in teil) setzen("feld", teil.feld === "created" ? "created" : null);
          if (teil.zeitraum) {
            setzen("von", teil.zeitraum.von);
            setzen("bis", teil.zeitraum.bis);
          }
          if ("status" in teil) setzen("status", (teil.status ?? []).join(","));
          if ("q" in teil) setzen("q", (teil.q ?? "").trim());
          if ("sort" in teil) setzen("sort", teil.sort);
          return neu;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const zuruecksetzen = useCallback(() => {
    setParams(
      (alt) => {
        const neu = new URLSearchParams(alt);
        for (const k of ["nutzer", "view", "feld", "von", "bis", "status", "q"]) neu.delete(k);
        return neu;
      },
      { replace: true },
    );
  }, [setParams]);

  const aktiv = !!(werte.nutzer || werte.view || werte.zeitraum.von || werte.zeitraum.bis || werte.status.length || werte.q);
  return { werte, setze, zuruecksetzen, aktiv, suche: params.toString() };
}

/** Parameter der Adresse für den Wechsel zwischen Board und Liste (nichts verlieren, Sortierung gehört der Ansicht). */
export function suchparameterBehalten(suche: string): string {
  const p = new URLSearchParams(suche);
  const neu = new URLSearchParams();
  for (const k of SCHLUESSEL) {
    if (k === "sort" || k === "status" || k === "q") continue;
    const v = p.get(k);
    if (v) neu.set(k, v);
  }
  const text = neu.toString();
  return text ? `?${text}` : "";
}
